import React from 'react';
import { ArrowUp, Crown, Gem, Zap } from 'lucide-react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { activeSubscriptionSelector, entitlementUnknownSelector } from '@common-reducers/UserSelectors';
import { selectFreeLimitPolicy } from '@common-reducers/free-limit-slice';
import { useRouter } from '@common-reducers/hooks/use-router';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import hs from '../header-status/header-status.module.scss';
import { PlanHoverCard, PlanHoverTarget, PlanTier } from '../header-status/plan-hover-card';
import { openPaymentSite } from '@common-services/utils';
import { isExtensionContextOrWhatsappView } from '@common-services/detect-context';
import { SubscriptionObject } from '../../../bt-common/services/subscription-object.class';

interface SubscriptionStatusBarProps {
  /**
   * If true, only show upgrade button for free/no subscription
   * If false, always show subscription status
   */
  compact?: boolean;
  
  /**
   * Custom click handler for upgrade button
   * If not provided, navigates to /account-subscription
   */
  onUpgradeClick?: () => void;

  /**
   * Show the status even when there's an active subscription
   */
  alwaysShow?: boolean;

  /**
   * Dev showcase only (/test-components): render this plan instead of the
   * signed-in user's. `null` renders the free / no-subscription badge. Leave
   * undefined in product code — the store subscription is used.
   */
  previewSubscription?: SubscriptionObject | null;
}

const planConfig = {
  free: {
    label: 'Free',
    // No per-tier `icon`: only the top tier renders a graphic (the crown tile).
    // Below it the CTA is the single graphic, so the tier glyph was retired —
    // the plan name already states the tier in words, and a chip with two
    // graphics has no focal point.
    order: 0,
  },
  basic: {
    label: 'Basic',
    order: 1,
  },
  standard: {
    label: 'Standard',
    order: 2,
  },
  pro: {
    icon: Crown,
    label: 'Pro',
    order: 3,
  },
  standardnew: {
    label: 'Standard',
    order: 2,
  },
};

/**
 * SubscriptionStatusBar - Displays current plan status and upgrade options
 * 
 * Features:
 * - Shows active subscription with appropriate styling
 * - Displays seat count for team plans
 * - Offers upgrade button for free/basic plans
 * - Compact mode for minimal space usage
 * 
 * @example
 * // Compact mode - only shows when action needed
 * <SubscriptionStatusBar compact />
 * 
 * @example
 * // Always visible with full details
 * <SubscriptionStatusBar alwaysShow />
 * 
 * @example
 * // Custom upgrade handler
 * <SubscriptionStatusBar onUpgradeClick={() => handleUpgrade()} />
 */
export const SubscriptionStatusBar = ({
  compact = false,
  onUpgradeClick,
  alwaysShow = false,
  previewSubscription,
}: SubscriptionStatusBarProps) => {
  const isPreview = previewSubscription !== undefined;
  const { t, i18n } = useTranslation(namespace);
  const isRtl = i18n.dir(i18n.language) === 'rtl';

  // The feature line the badge stopped printing comes back on hover, in our own
  // card. Same contract as the calendar's ChipHoverCard: a 320ms dwell so that
  // sweeping the pointer across the header does not flash cards, cleared
  // immediately on leave.
  const chipRef = React.useRef<HTMLDivElement>(null);
  const dwellRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hoverTarget, setHoverTarget] = React.useState<PlanHoverTarget | null>(null);
  const openHover = React.useCallback(() => {
    if (dwellRef.current) clearTimeout(dwellRef.current);
    dwellRef.current = setTimeout(() => {
      const rect = chipRef.current?.getBoundingClientRect();
      if (rect) setHoverTarget({ top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right });
    }, 320);
  }, []);
  const closeHover = React.useCallback(() => {
    if (dwellRef.current) clearTimeout(dwellRef.current);
    dwellRef.current = null;
    setHoverTarget(null);
  }, []);
  React.useEffect(() => () => { if (dwellRef.current) clearTimeout(dwellRef.current); }, []);
  const router = useRouter();
  const storeSubscription = useAppSelector(activeSubscriptionSelector);
  const activeSubscription = isPreview ? previewSubscription ?? undefined : storeSubscription;
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  const freeLimitPolicy = useAppSelector(selectFreeLimitPolicy);

  // The free-tier concurrent-slot cap, straight from the server-assigned policy (#131 T2) —
  // NOT hardcoded — so this badge tracks whatever arm the user is actually on (1, 3, ...)
  // instead of silently lying "1 parallel message" once the default limit changes.
  // concurrentLimit === null means "no concurrent cap" — the only case this `?? 1`
  // is reachable in. An unfetched/failed policy no longer lands here as 1: the
  // selector's fallback is FREE_BASELINE_POLICY (3), the same number the paywall
  // quotes, so the badge and the paywall can no longer disagree about the free limit.
  const freeParallelMessageText = t('status.parallelMessages', { count: freeLimitPolicy.concurrentLimit ?? 1 });

  // Determine plan tier based on entitlements (works for all subscription types)
  const hasUnlimitedMessages = activeSubscription?.messageLimit === Infinity;
  // FE#159 family — every `?? false` below reads the unentitled value when the
  // subscription has not loaded, so a paying customer's plan tier renders as free for the
  // duration. Hold the tier readout rather than assert a wrong one.
  const storeEntitlementUnknown = useAppSelector(entitlementUnknownSelector);
  const entitlementUnknown = !isPreview && storeEntitlementUnknown;
  const hasBulkFeature = activeSubscription?.hasBulk ?? false;
  const hasTasksFeature = activeSubscription?.hasTasks() ?? false;
  const hasSocketFeature = activeSubscription?.hasSocket() ?? false;
  const seatCount = activeSubscription?.seatCount ?? 1;
  const isActive = activeSubscription?.isActive;

  // Determine plan type and config based on features
  let planType: string;
  let config: any;
  
  if (!activeSubscription) {
    planType = 'free';
    config = planConfig.free as any;
  } else if (hasSocketFeature && hasUnlimitedMessages) {
    // Pro: Has offline socket + unlimited messages
    planType = 'pro';
    config = planConfig.pro as any;
  } else if ((hasTasksFeature || hasBulkFeature) && hasUnlimitedMessages) {
    // Standard: Has tasks/bulk + unlimited messages
    planType = 'standard';
    config = planConfig.standard as any;
  } else if (hasUnlimitedMessages) {
    // Basic: Has unlimited messages but limited features
    planType = 'basic';
    config = planConfig.basic as any;
  } else {
    // Free: Limited messages
    planType = 'free';
    config = planConfig.free as any;
  }

  
  // Check if user can upgrade (free or basic plans)
  const canUpgrade = !activeSubscription || config.order < 3;

  // Handle upgrade click
  const handleUpgrade = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else if (isExtensionContextOrWhatsappView() && accessToken) {
      openPaymentSite(accessToken);
    } else {
      router.navigate('/account-subscription', router.query);
    }
  };

  // The whole chip is the upgrade target, not just the small CTA inside it —
  // customers click the plan name and read a dead click as a broken button.
  const chipUpgradeProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick: handleUpgrade,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleUpgrade();
      }
    },
  };

  // In compact mode, show Free (needs upgrade), Basic (needs upgrade), Standard (can upgrade to Pro), and Pro (premium badge)
  // Only hide if explicitly not showing and there's no upgrade path
  
  // In non-compact mode without alwaysShow, only hide if there's nothing actionable (already at top tier)
  if (!alwaysShow && !compact && activeSubscription && config.order === 3) {
    // Pro users in non-compact mode without alwaysShow can be hidden
    // But we'll keep showing to display premium status
  }

  // FE#159 family — `!activeSubscription -> planType 'free'` cannot tell an absent
  // subscription from an unloaded one, so a paying customer's bar reads "Free Plan" with
  // a "Go Pro" button for the whole hydration window. MUST sit above EVERY return: the
  // compact variant (rendered by app-view's header) returns from the free branch below,
  // so a guard placed before the final return never runs for the pill the customer
  // actually sees. Caught in the browser, not by the unit tests.
  if (entitlementUnknown) return null;

  // Free plan or no subscription
  if (!activeSubscription || planType === 'free') {
    const planName = t('status.freePlan');
    return (
      <>
        <div
          ref={chipRef}
          className={`${hs.chip} ${hs.plan} ${hs.planFree} ${hs.planHasCta}`}
          // The accessible name keeps the retired feature line reachable for
          // assistive tech; the hover card is the sighted path. Neither is a
          // native `title` — see plan-hover-card.tsx.
          aria-label={`${planName} · ${freeParallelMessageText}`}
          onPointerEnter={openHover}
          onPointerLeave={closeHover}
          {...chipUpgradeProps}
        >
          <div className={`${hs.planName} ${hs.planNameQuiet}`}>{planName}</div>
          {/* Visual CTA only — the click bubbles to the chip, which owns it. */}
          <button
            type="button"
            className={hs.upgrade}
            tabIndex={-1}
            aria-hidden
            data-testid="upgrade-button"
          >
            {t('status.goPro')}
          </button>
        </div>
        {hoverTarget && (
          <PlanHoverCard
            target={hoverTarget}
            feature={freeParallelMessageText}
            variant="free"
            isRtl={isRtl}
          />
        )}
      </>
    );
  }

  // Active subscription (Basic, Standard, Pro).
  const isTopTier = config.order >= 3;

  // Get feature description based on actual entitlements
  const getFeatureDescription = () => {
    if (!activeSubscription) {
      return freeParallelMessageText;
    }
    
    // Show most impressive feature first
    if (hasSocketFeature) return t('status.unlimitedPlusOfflinePlusPremium');
    if (hasUnlimitedMessages && (hasTasksFeature || hasBulkFeature)) return t('status.unlimitedPlusAdvanced');
    if (hasUnlimitedMessages) return t('status.unlimitedMessages');
    if (seatCount > 1) return t('status.seats', { count: seatCount });
    
    // Fallback for limited plans
    const limit = activeSubscription.messageLimit;
    if (limit !== Infinity) {
      return t('status.parallelMessages', { count: limit });
    }
    
    return t('status.active');
  };

  const planName =
    config.label === 'Free' ? t('status.freePlan') :
    config.label === 'Basic' ? t('status.basicPlan') :
    config.label === 'Standard' ? t('status.standardPlan') :
    config.label === 'Pro' ? t('status.proPlan') :
    `${config.label} Plan`;

  // Paid tiers share one style and differ by colour: blue (Basic), teal
  // (Standard), violet (Pro). Each owns a tier glyph; the upgrade CTA on the
  // lower two is a small tinted icon, never a word, so the plan the customer is
  // paying for stays the subject of the chip.
  const tier: PlanTier = isTopTier ? 'pro' : planType === 'standard' ? 'standard' : 'basic';
  const tierClass = { basic: hs.planBasic, standard: hs.planStandard, pro: hs.planPro }[tier];
  const TierIcon = isTopTier ? Crown : tier === 'standard' ? Gem : Zap;

  return (
    <>
      <div
        ref={chipRef}
        className={`${hs.chip} ${hs.plan} ${tierClass} ${isTopTier ? '' : hs.planHasCta}`}
        aria-label={`${planName} · ${getFeatureDescription()}`}
        onPointerEnter={openHover}
        onPointerLeave={closeHover}
        {...(isTopTier ? {} : chipUpgradeProps)}
      >
        {/* The crown is the one filled glyph in the row, in Pro's own violet —
            the lower tiers wear outline glyphs. */}
        {isTopTier ? (
          <Crown className={hs.planMark} size={14} strokeWidth={1.75} fill="currentColor" fillOpacity={0.35} />
        ) : (
          <TierIcon className={hs.planMark} size={13} strokeWidth={2} />
        )}
        <div className={hs.planName}>{planName}</div>

        {!isTopTier && (
          <button
            type="button"
            className={hs.upgradeIcon}
            tabIndex={-1}
            aria-hidden
            data-testid="upgrade-button"
          >
            <ArrowUp size={12} strokeWidth={2.75} />
          </button>
        )}
      </div>
      {hoverTarget && (
        <PlanHoverCard
          target={hoverTarget}
          feature={getFeatureDescription()}
          // Only the top tier gets the second line: it is reassurance, and a
          // tier with an upgrade left to sell has better things to say.
          status={isTopTier ? t('status.active') : undefined}
          variant={tier}
          isRtl={isRtl}
        />
      )}
    </>
  );
};

export default SubscriptionStatusBar;

